import type { PublicListingDetailsV3 } from 'types/listingV3';
import { parseTextFromHtmlString } from 'utils';
import getZonedDateString from 'utils/date/getZonedDateString';
import toZonedIsoDateTime from 'utils/date/toZonedIsoDateTime';

const SCHEMA_URL = 'https://schema.org';

type JsonLd = Record<string, unknown>;

interface ListingJsonLdParams {
  brandName?: string;
  brandUrl: string;
  image?: string;
  listing: PublicListingDetailsV3;
  today?: string;
  url: string;
}

const toDescription = (listing: PublicListingDetailsV3): string =>
  parseTextFromHtmlString(
    listing.description || listing.summary || listing.title || '',
  );

const toOrganization = (brandName: string | undefined, brandUrl: string) => ({
  '@type': 'Organization',
  name: brandName,
  url: brandUrl,
});

/** A "from" price as an AggregateOffer, or nothing when the price is hidden or unknown. */
const toOffers = (listing: PublicListingDetailsV3, url: string) => {
  if (listing.hidePrice || listing.startingPrice == null || !listing.currency) {
    return undefined;
  }
  return {
    '@type': 'AggregateOffer',
    lowPrice: listing.startingPrice,
    priceCurrency: listing.currency,
    url,
  };
};

const toPlace = (listing: PublicListingDetailsV3) =>
  listing.location
    ? {
        '@type': 'Place',
        name: listing.location.name,
        geo: {
          '@type': 'GeoCoordinates',
          latitude: listing.location.latitude,
          longitude: listing.location.longitude,
        },
      }
    : undefined;

/** Drops undefined fields so the JSON-LD never carries empty keys. */
const compact = (data: JsonLd): JsonLd =>
  Object.fromEntries(
    Object.entries(data).filter(([, value]) => value !== undefined),
  );

/**
 * The next date the event runs (today or later at the venue; the earliest date when all are past) at
 * its earliest start time, with the venue's UTC offset ON that date: `2026-11-09T18:00:00-08:00`.
 * Without a timezone the offset is omitted (a local time) rather than guessed. Undefined when the event
 * has no dates or times yet.
 */
export const deriveEventStartDate = (
  listing: PublicListingDetailsV3,
  today = getZonedDateString(listing.location?.timezone),
): string | undefined => {
  const dates = [...listing.eventDates].sort();
  const [startTime] = [...listing.eventStartTimes].sort();
  const date = dates.find((d) => d >= today) ?? dates[0];
  if (!date || !startTime) return undefined;

  return (
    toZonedIsoDateTime(`${date}T${startTime}`, listing.location?.timezone) ??
    undefined
  );
};

/** schema.org `Product` for a reservable collection (cabanas, tables, rooms): name, image, "from" price. */
export const buildCollectionJsonLd = ({
  brandName,
  brandUrl,
  image,
  listing,
  url,
}: ListingJsonLdParams): JsonLd =>
  compact({
    '@context': SCHEMA_URL,
    '@type': 'Product',
    url,
    name: listing.title ?? undefined,
    description: toDescription(listing),
    image,
    brand: toOrganization(brandName, brandUrl),
    offers: toOffers(listing, url),
  });

/**
 * schema.org `Event` for a ticketed event. Google requires `startDate` on an Event, so without one
 * (no dates published yet) nothing is emitted rather than an invalid Event.
 */
export const buildEventJsonLd = ({
  brandName,
  brandUrl,
  image,
  listing,
  today,
  url,
}: ListingJsonLdParams): JsonLd | null => {
  const startDate = deriveEventStartDate(listing, today);
  if (!startDate) return null;

  return compact({
    '@context': SCHEMA_URL,
    '@type': 'Event',
    url,
    name: listing.title ?? undefined,
    description: toDescription(listing),
    image,
    startDate,
    eventStatus: `${SCHEMA_URL}/EventScheduled`,
    eventAttendanceMode: `${SCHEMA_URL}/OfflineEventAttendanceMode`,
    location: toPlace(listing),
    organizer: toOrganization(brandName, brandUrl),
    offers: toOffers(listing, url),
  });
};
